import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, where, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import InputField from '../../components/ui/InputField';
import PlusIcon from '../../components/icons/PlusIcon';
import EditIcon from '../../components/icons/EditIcon';
import TrashIcon from '../../components/icons/TrashIcon';
import CloseIcon from '../../components/icons/CloseIcon';
import BellIcon from '../../components/icons/BellIcon';
import { createSystemNotification } from '../../utils/notifications';

interface Announcement {
  id: string;
  title: string;
  content: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  targetAudience: 'all' | 'teachers' | 'students';
  displayLocation?: 'all' | 'home' | 'class-student' | 'class-teacher';
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  expiresAt?: Date;
}

const NotificationManagement: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [openAnnouncementDialog, setOpenAnnouncementDialog] = useState(false);
  const [openNotificationDialog, setOpenNotificationDialog] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState<Announcement | null>(null);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    targetAudience: 'all' as 'all' | 'teachers' | 'students',
    displayLocation: 'all' as 'all' | 'home' | 'class-student' | 'class-teacher',
    expiresAt: ''
  });
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    priority: 'medium' as 'low' | 'medium' | 'high' | 'urgent',
    targetAudience: 'all' as 'all' | 'teachers' | 'students',
    actionUrl: ''
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const announcementsRef = collection(db, 'announcements');
      const q = query(announcementsRef, orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      
      const announcementsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
        updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        expiresAt: doc.data().expiresAt?.toDate()
      })) as Announcement[];
      
      setAnnouncements(announcementsList);
    } catch (error) {
      console.error('공지사항 목록 가져오기 오류:', error);
      setError('공지사항 목록을 불러오는 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAnnouncement = async () => {
    if (!announcementForm.title.trim() || !announcementForm.content.trim()) {
      setError('제목과 내용은 필수입니다.');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const announcementData = {
        title: announcementForm.title,
        content: announcementForm.content,
        priority: announcementForm.priority,
        targetAudience: announcementForm.targetAudience,
        displayLocation: announcementForm.displayLocation,
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        expiresAt: announcementForm.expiresAt ? Timestamp.fromDate(new Date(announcementForm.expiresAt)) : null
      };

      if (editingAnnouncement) {
        await updateDoc(doc(db, 'announcements', editingAnnouncement.id), {
          ...announcementData,
          createdAt: editingAnnouncement.createdAt
        });
        setSuccess('공지사항이 수정되었습니다.');
      } else {
        await addDoc(collection(db, 'announcements'), announcementData);
        setSuccess('공지사항이 생성되었습니다.');
      }

      setOpenAnnouncementDialog(false);
      setAnnouncementForm({
        title: '',
        content: '',
        priority: 'medium',
        targetAudience: 'all',
        displayLocation: 'all',
        expiresAt: ''
      });
      setEditingAnnouncement(null);
      await fetchAnnouncements();
    } catch (error) {
      console.error('공지사항 저장 오류:', error);
      setError('공지사항 저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnnouncement = async (id: string) => {
    if (!window.confirm('정말로 이 공지사항을 삭제하시겠습니까?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteDoc(doc(db, 'announcements', id));
      setSuccess('공지사항이 삭제되었습니다.');
      await fetchAnnouncements();
    } catch (error) {
      console.error('공지사항 삭제 오류:', error);
      setError('공지사항 삭제 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAnnouncement = async (announcement: Announcement) => {
    try {
      await updateDoc(doc(db, 'announcements', announcement.id), {
        isActive: !announcement.isActive,
        updatedAt: serverTimestamp()
      });
      await fetchAnnouncements();
    } catch (error) {
      console.error('공지사항 상태 변경 오류:', error);
      setError('공지사항 상태 변경 중 오류가 발생했습니다.');
    }
  };

  const handleSendNotification = async () => {
    if (!notificationForm.title.trim() || !notificationForm.message.trim()) {
      setError('제목과 메시지는 필수입니다.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // 모든 사용자 가져오기 (users 컬렉션과 schools/{schoolId}/teachers, schools/{schoolId}/students)
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(usersRef);
      const usersList = usersSnapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data()
      }));

      // 대상 필터링
      let targetUsers = usersList;
      if (notificationForm.targetAudience === 'teachers') {
        targetUsers = usersList.filter((user: any) => 
          user.role === 'teacher' || user.role === 'superAdmin' || user.role === 'schoolAdmin'
        );
      } else if (notificationForm.targetAudience === 'students') {
        targetUsers = usersList.filter((user: any) => user.role === 'student');
      }

      // 각 사용자에게 알림 생성
      const promises = targetUsers.map((user: any) => 
        createSystemNotification(
          user.uid,
          notificationForm.title,
          notificationForm.message,
          notificationForm.priority,
          notificationForm.actionUrl || undefined
        ).catch((err) => {
          console.error(`사용자 ${user.uid}에게 알림 전송 실패:`, err);
          return null;
        })
      );

      await Promise.all(promises);
      setSuccess(`${targetUsers.length}명에게 알림이 전송되었습니다.`);
      setOpenNotificationDialog(false);
      setNotificationForm({
        title: '',
        message: '',
        priority: 'medium',
        targetAudience: 'all',
        actionUrl: ''
      });
    } catch (error) {
      console.error('알림 전송 오류:', error);
      setError('알림 전송 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditAnnouncement = (announcement: Announcement) => {
    setEditingAnnouncement(announcement);
    setAnnouncementForm({
      title: announcement.title,
      content: announcement.content,
      priority: announcement.priority,
      targetAudience: announcement.targetAudience,
      displayLocation: announcement.displayLocation || 'all',
      expiresAt: announcement.expiresAt ? announcement.expiresAt.toISOString().split('T')[0] : ''
    });
    setOpenAnnouncementDialog(true);
  };

  const getPriorityBadge = (priority: string) => {
    const styles = {
      low: { bg: '#F5F5F5', color: '#4C4C4C' },
      medium: { bg: '#E3F2FD', color: '#1976D2' },
      high: { bg: '#FFF3E0', color: '#F57C00' },
      urgent: { bg: '#FFEBEE', color: '#D32F2F' }
    };
    const style = styles[priority as keyof typeof styles] || styles.medium;
    
    return (
      <div style={{
        display: 'inline-flex',
        padding: '4px 12px',
        borderRadius: '12px',
        backgroundColor: style.bg,
        color: style.color,
        fontSize: '12px',
        fontWeight: 500
      }}>
        {priority === 'low' ? '낮음' : priority === 'medium' ? '보통' : priority === 'high' ? '높음' : '긴급'}
      </div>
    );
  };

  const getTargetAudienceLabel = (audience: string) => {
    switch (audience) {
      case 'all': return '전체';
      case 'teachers': return '교사';
      case 'students': return '학생';
      default: return audience;
    }
  };

  const getDisplayLocationLabel = (location: string) => {
    switch (location) {
      case 'all': return '전체 페이지';
      case 'home': return '홈';
      case 'class-student': return '학생 학급';
      case 'class-teacher': return '교사 학급';
      default: return location;
    }
  };

  if (loading && announcements.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px' 
      }}>
        <div style={{ fontSize: '16px', color: 'var(--Color-text-normal, #161616)' }}>
          로딩 중...
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div
          style={{
            fontSize: '24px',
            fontWeight: 600,
            lineHeight: '36px',
            color: 'var(--Color-text-strong, #0D0D0D)'
          }}
        >
          알림 관리
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            variant="secondary"
            size="medium"
            leftIcon={<BellIcon width={16} height={16} fill="var(--Color-text-normal, #161616)" />}
            onClick={() => setOpenNotificationDialog(true)}
          >
            알림 전송
          </Button>
          <Button
            variant="primary"
            size="medium"
            leftIcon={<PlusIcon width={16} height={16} fill="white" />}
            onClick={() => {
              setEditingAnnouncement(null);
              setAnnouncementForm({
                title: '',
                content: '',
                priority: 'medium',
                targetAudience: 'all',
                displayLocation: 'all',
                expiresAt: ''
              });
              setOpenAnnouncementDialog(true);
            }}
          >
            공지사항 추가
          </Button>
        </div>
      </div>

      {/* 알림 메시지 */}
      {error && (
        <Card
          padding="12px 16px"
          background="#FFF5F5"
          style={{ border: '1px solid #FFE0E0' }}
        >
          <div style={{ fontSize: '14px', color: '#FF3838' }}>{error}</div>
        </Card>
      )}

      {success && (
        <Card
          padding="12px 16px"
          background="#F0FFF4"
          style={{ border: '1px solid #C6F6D5' }}
        >
          <div style={{ fontSize: '14px', color: '#38A169' }}>{success}</div>
        </Card>
      )}

      {/* 공지사항 목록 */}
      {announcements.length === 0 ? (
        <Card padding="40px" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', color: 'var(--Color-text-normal, #161616)' }}>
            등록된 공지사항이 없습니다.
          </div>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {announcements.map((announcement) => (
            <Card key={announcement.id} padding="20px" gap="12px">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <div
                      style={{
                        fontSize: '16px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)'
                      }}
                    >
                      {announcement.title}
                    </div>
                    {getPriorityBadge(announcement.priority)}
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--Color-text-normal, #161616)',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: 'var(--Color-fill-hover, #F5F5F5)'
                    }}>
                      {getTargetAudienceLabel(announcement.targetAudience)}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: 'var(--Color-text-normal, #161616)',
                      padding: '4px 12px',
                      borderRadius: '12px',
                      backgroundColor: 'var(--Color-fill-hover, #F5F5F5)'
                    }}>
                      {getDisplayLocationLabel(announcement.displayLocation || 'all')}
                    </div>
                    {announcement.isActive ? (
                      <div style={{
                        fontSize: '12px',
                        color: '#38A169',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        backgroundColor: '#F0FFF4'
                      }}>
                        활성
                      </div>
                    ) : (
                      <div style={{
                        fontSize: '12px',
                        color: '#989898',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        backgroundColor: '#F5F5F5'
                      }}>
                        비활성
                      </div>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: '14px',
                      color: 'var(--Color-text-normal, #161616)',
                      lineHeight: '21px',
                      whiteSpace: 'pre-wrap',
                      marginBottom: '8px'
                    }}
                  >
                    {announcement.content}
                  </div>
                  <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--Color-text-normal, #161616)' }}>
                    <div>생성일: {announcement.createdAt.toLocaleDateString('ko-KR')}</div>
                    {announcement.expiresAt && (
                      <div>만료일: {announcement.expiresAt.toLocaleDateString('ko-KR')}</div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => handleToggleAnnouncement(announcement)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: '6px',
                      border: 'none',
                      backgroundColor: announcement.isActive ? '#FFF5F5' : '#F0FFF4',
                      color: announcement.isActive ? '#FF3838' : '#38A169',
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    {announcement.isActive ? '비활성화' : '활성화'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleEditAnnouncement(announcement)}
                    style={{
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      borderRadius: '6px',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--Color-fill-hover, #F5F5F5)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <EditIcon width={16} height={16} color="#4C4C4C" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteAnnouncement(announcement.id)}
                    style={{
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'pointer',
                      borderRadius: '6px',
                      transition: 'background-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#FFF5F5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <TrashIcon width={14} height={16} color="#FF3838" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* 공지사항 생성/수정 다이얼로그 */}
      {openAnnouncementDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            fontFamily: 'Pretendard, sans-serif'
          }}
          onClick={() => {
            setOpenAnnouncementDialog(false);
            setEditingAnnouncement(null);
          }}
        >
          <div
            style={{
              width: '90%',
              maxWidth: '600px',
              padding: '0',
              borderRadius: '16px',
              background: 'var(--Color-background-normal, white)',
              boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
              position: 'relative',
              overflow: 'hidden',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          >
            {/* 다이얼로그 헤더 */}
            <div style={{
              padding: '32px',
              borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{
                fontSize: '20px',
                fontWeight: 600,
                color: 'var(--Color-text-strong, #0D0D0D)'
              }}>
                {editingAnnouncement ? '공지사항 수정' : '공지사항 추가'}
              </div>
              <button
                type="button"
                onClick={() => {
                  setOpenAnnouncementDialog(false);
                  setEditingAnnouncement(null);
                }}
                style={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--Color-fill-hover, #F5F5F5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <CloseIcon width={20} height={20} />
              </button>
            </div>

            {/* 다이얼로그 내용 */}
            <div style={{
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              <InputField
                label="제목"
                value={announcementForm.title}
                onChange={(value) => setAnnouncementForm(prev => ({ ...prev, title: value }))}
                required
                width="100%"
              />

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#161616',
                  marginBottom: '8px'
                }}>
                  내용
                </label>
                <textarea
                  value={announcementForm.content}
                  onChange={(e) => setAnnouncementForm(prev => ({ ...prev, content: e.target.value }))}
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#161616',
                    backgroundColor: 'white',
                    border: '1.2px solid #F0F0F0',
                    borderRadius: '8px',
                    outline: 'none',
                    fontFamily: 'Pretendard, sans-serif',
                    resize: 'vertical'
                  }}
                  placeholder="공지사항 내용을 입력하세요"
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#161616',
                  marginBottom: '8px'
                }}>
                  우선순위
                </label>
                <select
                  value={announcementForm.priority}
                  onChange={(e) => setAnnouncementForm(prev => ({ ...prev, priority: e.target.value as any }))}
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#161616',
                    backgroundColor: 'white',
                    border: '1.2px solid #F0F0F0',
                    borderRadius: '8px',
                    outline: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Pretendard, sans-serif'
                  }}
                >
                  <option value="low">낮음</option>
                  <option value="medium">보통</option>
                  <option value="high">높음</option>
                  <option value="urgent">긴급</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#161616',
                  marginBottom: '8px'
                }}>
                  노출 위치
                </label>
                <select
                  value={announcementForm.displayLocation}
                  onChange={(e) => setAnnouncementForm(prev => ({
                    ...prev,
                    displayLocation: e.target.value as 'all' | 'home' | 'class-student' | 'class-teacher'
                  }))}
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#161616',
                    backgroundColor: 'white',
                    border: '1.2px solid #F0F0F0',
                    borderRadius: '8px',
                    outline: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Pretendard, sans-serif'
                  }}
                >
                  <option value="all">전체 페이지</option>
                  <option value="home">홈</option>
                  <option value="class-student">학생 학급</option>
                  <option value="class-teacher">교사 학급</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#161616',
                  marginBottom: '8px'
                }}>
                  대상
                </label>
                <select
                  value={announcementForm.targetAudience}
                  onChange={(e) => setAnnouncementForm(prev => ({ ...prev, targetAudience: e.target.value as any }))}
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#161616',
                    backgroundColor: 'white',
                    border: '1.2px solid #F0F0F0',
                    borderRadius: '8px',
                    outline: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Pretendard, sans-serif'
                  }}
                >
                  <option value="all">전체</option>
                  <option value="teachers">교사</option>
                  <option value="students">학생</option>
                </select>
              </div>

              <InputField
                label="만료일 (선택사항)"
                type="date"
                value={announcementForm.expiresAt}
                onChange={(value) => setAnnouncementForm(prev => ({ ...prev, expiresAt: value }))}
                width="100%"
              />
            </div>

            {/* 다이얼로그 푸터 */}
            <div style={{
              padding: '24px 32px',
              borderTop: '1px solid var(--Color-line-normal, #F0F0F0)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <Button
                variant="cancel"
                onClick={() => {
                  setOpenAnnouncementDialog(false);
                  setEditingAnnouncement(null);
                }}
                disabled={loading}
              >
                취소
              </Button>
              <Button
                variant="primary"
                onClick={handleCreateAnnouncement}
                disabled={loading}
              >
                {loading ? '처리 중...' : (editingAnnouncement ? '수정' : '추가')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 알림 전송 다이얼로그 */}
      {openNotificationDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            fontFamily: 'Pretendard, sans-serif'
          }}
          onClick={() => setOpenNotificationDialog(false)}
        >
          <div
            style={{
              width: '90%',
              maxWidth: '600px',
              padding: '0',
              borderRadius: '16px',
              background: 'var(--Color-background-normal, white)',
              boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)',
              position: 'relative',
              overflow: 'hidden',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
          >
            {/* 다이얼로그 헤더 */}
            <div style={{
              padding: '32px',
              borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{
                fontSize: '20px',
                fontWeight: 600,
                color: 'var(--Color-text-strong, #0D0D0D)'
              }}>
                알림 전송
              </div>
              <button
                type="button"
                onClick={() => setOpenNotificationDialog(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: 'pointer',
                  borderRadius: '6px',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--Color-fill-hover, #F5F5F5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <CloseIcon width={20} height={20} />
              </button>
            </div>

            {/* 다이얼로그 내용 */}
            <div style={{
              padding: '32px',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              <InputField
                label="제목"
                value={notificationForm.title}
                onChange={(value) => setNotificationForm(prev => ({ ...prev, title: value }))}
                required
                width="100%"
              />

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#161616',
                  marginBottom: '8px'
                }}>
                  메시지
                </label>
                <textarea
                  value={notificationForm.message}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, message: e.target.value }))}
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#161616',
                    backgroundColor: 'white',
                    border: '1.2px solid #F0F0F0',
                    borderRadius: '8px',
                    outline: 'none',
                    fontFamily: 'Pretendard, sans-serif',
                    resize: 'vertical'
                  }}
                  placeholder="알림 메시지를 입력하세요"
                />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#161616',
                  marginBottom: '8px'
                }}>
                  우선순위
                </label>
                <select
                  value={notificationForm.priority}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, priority: e.target.value as any }))}
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#161616',
                    backgroundColor: 'white',
                    border: '1.2px solid #F0F0F0',
                    borderRadius: '8px',
                    outline: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Pretendard, sans-serif'
                  }}
                >
                  <option value="low">낮음</option>
                  <option value="medium">보통</option>
                  <option value="high">높음</option>
                  <option value="urgent">긴급</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#161616',
                  marginBottom: '8px'
                }}>
                  대상
                </label>
                <select
                  value={notificationForm.targetAudience}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, targetAudience: e.target.value as any }))}
                  style={{
                    width: '100%',
                    height: '48px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#161616',
                    backgroundColor: 'white',
                    border: '1.2px solid #F0F0F0',
                    borderRadius: '8px',
                    outline: 'none',
                    cursor: 'pointer',
                    fontFamily: 'Pretendard, sans-serif'
                  }}
                >
                  <option value="all">전체</option>
                  <option value="teachers">교사</option>
                  <option value="students">학생</option>
                </select>
              </div>

              <InputField
                label="링크 URL (선택사항)"
                value={notificationForm.actionUrl}
                onChange={(value) => setNotificationForm(prev => ({ ...prev, actionUrl: value }))}
                width="100%"
                placeholder="/class/teacher/..."
              />
            </div>

            {/* 다이얼로그 푸터 */}
            <div style={{
              padding: '24px 32px',
              borderTop: '1px solid var(--Color-line-normal, #F0F0F0)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <Button
                variant="cancel"
                onClick={() => setOpenNotificationDialog(false)}
                disabled={loading}
              >
                취소
              </Button>
              <Button
                variant="primary"
                onClick={handleSendNotification}
                disabled={loading}
                leftIcon={loading ? undefined : <BellIcon width={16} height={16} fill="white" />}
              >
                {loading ? '전송 중...' : '알림 전송'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationManagement;
